export default {
    name: 'CodeInput',

    props: {
        value: {
            type: String,
            default: '',
        },
        error: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        length: {
            type: Number,
            default: 6,
        },
    },

    data() {
        return {
            digits: Array(this.length).fill(''),
        };
    },

    computed: {
        isComplete() {
            return this.digits.every(d => d !== '');
        },
        codeValue() {
            return this.digits.join('');
        },
    },

    watch: {
        value: {
            immediate: true,
            handler(newVal) {
                if (newVal !== this.codeValue) {
                    this.setCodeFromValue(newVal);
                }
            },
        },
    },

    methods: {
        /**
         * Handle input event - auto-advance to next input and distribute autofill content
         */
        handleInput(event, index) {
            const numericValue = this.getNumericValue(event.target.value);

            if (numericValue.length > 1) {
                // iOS one-time-code autofill injects the full code into the focused field.
                const startIndex = numericValue.length >= this.length ? 0 : index;
                this.applyCode(numericValue, startIndex);
                return;
            }

            if (numericValue.length > 0) {
                this.$set(this.digits, index, numericValue);

                // Auto-advance to next input
                if (index < this.length - 1) {
                    this.$nextTick(() => {
                        this.focusInput(index + 1);
                    });
                }
            } else {
                this.$set(this.digits, index, '');
            }

            this.emitUpdate();
        },

        /**
         * Handle keydown events - backspace navigation and arrow keys
         */
        handleKeydown(event, index) {
            const key = event.key;

            if (key === 'Backspace') {
                if (this.digits[index] === '' && index > 0) {
                    // If current input is empty, go to previous and clear it
                    event.preventDefault();
                    this.$set(this.digits, index - 1, '');
                    this.focusInput(index - 1);
                    this.emitUpdate();
                } else if (this.digits[index] !== '') {
                    // Clear current input
                    this.$set(this.digits, index, '');
                    this.emitUpdate();
                }
            } else if (key === 'ArrowLeft' && index > 0) {
                event.preventDefault();
                this.focusInput(index - 1);
            } else if (key === 'ArrowRight' && index < this.length - 1) {
                event.preventDefault();
                this.focusInput(index + 1);
            } else if (key === 'Enter' && this.isComplete) {
                this.$emit('submit');
            }
        },

        /**
         * Handle paste event - fill all inputs from pasted content
         */
        handlePaste(event) {
            event.preventDefault();

            const pastedData = (event.clipboardData || window.clipboardData).getData('text');
            this.applyCode(pastedData);
        },

        /**
         * Select content on focus for easy replacement
         */
        handleFocus(event) {
            if (event.target.value) {
                event.target.select();
            }
        },

        getNumericValue(value) {
            return (value || '').replace(/\D/g, '');
        },

        applyCode(value, startIndex = 0) {
            const numericValue = this.getNumericValue(value).slice(0, this.length - startIndex);

            if (numericValue.length === 0) {
                return;
            }

            for (let offset = 0; offset < numericValue.length; offset++) {
                this.$set(this.digits, startIndex + offset, numericValue[offset]);
            }

            if (startIndex === 0) {
                for (let i = numericValue.length; i < this.length; i++) {
                    this.$set(this.digits, i, '');
                }
            }

            this.$nextTick(() => {
                this.focusNextAvailableInput(startIndex);
            });

            this.emitUpdate();
        },

        focusNextAvailableInput(startIndex = 0) {
            const nextEmptyIndex = this.digits.findIndex((digit, index) => index >= startIndex && digit === '');

            if (nextEmptyIndex !== -1) {
                this.focusInput(nextEmptyIndex);
                return;
            }

            this.focusInput(this.length - 1);
        },

        /**
         * Focus a specific input by index
         */
        focusInput(index) {
            const inputs = this.$refs.inputs;
            if (inputs && inputs[index]) {
                inputs[index].focus();
            }
        },

        /**
         * Public method: focus the first input
         */
        focusFirst() {
            this.focusInput(0);
        },

        /**
         * Public method: clear all inputs
         */
        clear() {
            this.digits = Array(this.length).fill('');
            this.emitUpdate();
            this.$nextTick(() => {
                this.focusFirst();
            });
        },

        /**
         * Set digits from external value
         */
        setCodeFromValue(value) {
            const chars = (value || '').split('');
            for (let i = 0; i < this.length; i++) {
                this.$set(this.digits, i, chars[i] || '');
            }
        },

        /**
         * Emit update events
         */
        emitUpdate() {
            const code = this.codeValue;
            this.$emit('input', code);
            this.$emit('update:value', code);

            if (this.isComplete) {
                this.$emit('complete', code);
            }
        },
    },
};



// WEBPACK FOOTER //
// ./src/components/generics/code-input/code-input.js